/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { connect } from 'react-redux';
import { Column } from '../../layout/flexbox';
import { IAppState } from '../../core/redux/IAppState';
import { getError, getLoading } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { ApiError } from '../../core/redux/api/apiError';
import { loadFaqsAction } from './loadFaqsAction';
import { LargeText, PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE } from '../../styles/styles';
import { Loader } from '../../forms/elements/loader/Loader';
import { css } from '@emotion/react';
import ErrorView from '../../forms/ErrorView';
import { isNilOrEmpty } from '../../language/module';
import { IKON_BLUE } from '../../styles/colors';
import { clearFaqsAction } from './clearFaqsAction';
import { getResortDetailsAction } from '../../resortDetails/getResortDetailsAction';
import { AppDocItem } from '../appDocItem';
import AppDocItemView from '../AppDocItemView';
import { canShowDocItem } from '../module';
import { getResource } from '../../language/resources/getResource';


const noFaqError = css`
 display: block;
 margin: 25px auto;
 text-align: center;
 color: ${IKON_BLUE};
 ${LargeText};
`;

const faqLoaderAlignment = css`
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  padding: 25px 0;
`;

const faqLoader = css`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
`;

const loadingFaqMsg = css`
  display: block; 
  ${LargeText};
`;


type Props = {
  resortAbbrev?: string;
  isLoadingResorts?: boolean;
  faqs?: AppDocItem[];
  isLoading?: boolean;
  error?: ApiError;
  resources: Record<string, any>;
  loadFaqs?: () => void;
  clearFaqs?: () => void;
  fetchResorts?: () => void;
};


/**
 * FaqPage: a component that initiates a fetch for the Ikon FAQs, then renders them or their fetch result. Each FAQ is
 * expandable and happily renders rich text.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState) => {
    const resortAbbrev = state.selectedResort.resortAbbreviation || '';
    const userRole     = state.userState.role!;
    const isDtw        = state.resortToolsConfig.enableDtw.includes(state.userState.role!);
    const resources    = state.locale;

    return {
      resortAbbrev: resortAbbrev,
      faqs:         state.faqs && state.faqs.filter(faq => canShowDocItem(isDtw, resortAbbrev, userRole, faq)),
      isLoading:    !!getLoading(state, ACTION_TYPE.LOAD_FAQS_REQUEST, ACTION_TYPE.LOAD_FAQS_REQUEST),
      error:        getError(state, ACTION_TYPE.LOAD_FAQS_REQUEST, ACTION_TYPE.LOAD_FAQS_REQUEST),
      resources:    resources,
    };
  },
  (dispatch) => ({
    loadFaqs:     () => dispatch(loadFaqsAction()),
    clearFaqs:    () => dispatch(clearFaqsAction()),
    fetchResorts: () => dispatch(getResortDetailsAction()),
  }),
)(class FaqPage extends React.PureComponent<Props> {
  public componentDidMount(): void {
    this.props.loadFaqs!();

    if (isNilOrEmpty(this.props.resortAbbrev)) {
      this.props.fetchResorts!();
    }
  }


  public componentWillUnmount(): void {
    this.props.clearFaqs!();
  }


  public render = () => (
    <Column rootCss={PAGE_COLUMN}>
      <span css={PAGE_TITLE}>{getResource(this.props.resources, 'FAQ_PAGE_TITLE')}</span>
      <div css={PAGE_CONTENT}>
        { // Loading
          this.props.isLoading
          && <div css={faqLoaderAlignment}>
            <Loader rootCss={faqLoader}/>
            <span css={loadingFaqMsg}>{getResource(this.props.resources, 'FAQ_PAGE_LOADING_FAQ')}</span>
          </div>
        }
        {/* Error */}
        <ErrorView error={this.props.isLoading
                          ? undefined
                          : this.props.error && getResource(this.props.resources, 'FAQ_PAGE_FETCH_FAILED')}/>
        { // Empty
          isNilOrEmpty(this.props.faqs)
          && !this.props.isLoading
          && !this.props.error
          && <span css={noFaqError}>{getResource(this.props.resources, 'FAQ_PAGE_NO_FAQ')}</span>
        }
        { // FAQs (Data)
          !isNilOrEmpty(this.props.faqs) && !this.props.error && this.props.faqs!.map(
            faq => <AppDocItemView key={faq.title} docItem={faq} startCollapsed={true}/>,
          )
        }
      </div>
    </Column>
  );
});
